import { FunctionComponent, useMemo, useState, useEffect } from 'react';
import { Radio, Box } from '@mui/material';
import { McGeneralDiseaseGroup } from 'src/api/medcards/mcGeneralDiseaseService';
import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/app/hooks';
import { nurseService } from 'src/api/nurse/nurseService';
import {
  healthInfoUserViewService,
  FIXED_TYPE_IDS
} from 'src/api/medcards/healthInfoUserViewService';

interface McListProps {
  patientId?: string | number;
  generalDiseasesData?: {
    counts: Record<string, number>;
    groups: McGeneralDiseaseGroup[];
  } | null;
  gender?: string;
  isPregnant?: boolean;
  onOpenGhiModal?: (section?: string) => void;
  refreshTrigger?: number; // Modal kapandığında refresh için
}

interface CardConfig {
  typename: string;
  title: string;
  showRadio?: boolean; // Sadece Alerjiler için
  sectionKey: string; // Modalda açılacak section key'i
  alwaysVisible?: boolean; // Veri olmasa da göster
  genderSpecific?: 'female' | 'male'; // Sadece belirli cinsiyet için
  typeId?: number; // API type ID'si
  count?: number;
  label?: string;
  section?: string;
}

// Genel Sağlık Bilgileri hızlı özet kartı (medcard orId 2571 görünümü)
const McList: FunctionComponent<McListProps> = ({ patientId, generalDiseasesData, gender, isPregnant, onOpenGhiModal, refreshTrigger }) => {
  const { t } = useTranslate();
  const { user } = useAppSelector((state: any) => state.auth);

  // API'den hasta bilgilerini çekmek için state
  const [patientData, setPatientData] = useState<any>(null);

  // Kullanıcı tercihlerini saklamak için state
  const [userPreferences, setUserPreferences] = useState<Set<number>>(new Set(FIXED_TYPE_IDS));
  const [orderedPreferences, setOrderedPreferences] = useState<number[]>([1, 2, 10]); // API'den gelen sıra
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Kullanıcı ID'sini Redux state'den al
  const getUserId = (): number | null => {
    return user?.id || null;
  };

  // Kullanıcı tercihlerini API'den çek
  useEffect(() => {
    const fetchUserPreferences = async () => {
      const userId = getUserId();

      if (!userId) {
        setUserPreferences(new Set(FIXED_TYPE_IDS));
        setOrderedPreferences([1, 2, 10]);
        setPreferencesLoaded(true);
        return;
      }

      try {
        const response = await healthInfoUserViewService.getUserPreferences(userId);
        if (response.issuccess && response.data?.typeids) {
          const typeIds = response.data.typeids;

          // Gebelik durumu (10) her zaman 3. sırada olmalı: 1, 2, 10, ...diğerleri
          const reorderedTypeIds = [...typeIds];
          const pregnancyIndex = reorderedTypeIds.indexOf(10);

          if (pregnancyIndex !== -1 && pregnancyIndex !== 2) {
            // Gebelik varsa ve 3. sırada değilse, yeniden sırala
            reorderedTypeIds.splice(pregnancyIndex, 1); // Mevcut yerinden çıkar

            // 1 ve 2'den sonra ekle (index 2)
            const insertIndex = Math.min(2, reorderedTypeIds.length);
            reorderedTypeIds.splice(insertIndex, 0, 10);
          }

          setUserPreferences(new Set(typeIds)); // Set için orijinal liste
          setOrderedPreferences(reorderedTypeIds); // Sıralama için düzenlenmiş liste
        } else {
          setUserPreferences(new Set(FIXED_TYPE_IDS));
          setOrderedPreferences([1, 2, 10]);
        }
      } catch (error) {
        console.error('❌ MC-LIST: Kullanıcı tercihleri alınamadı:', error);
        setUserPreferences(new Set(FIXED_TYPE_IDS));
        setOrderedPreferences([1, 2, 10]);
      } finally {
        setPreferencesLoaded(true);
      }
    };

    fetchUserPreferences();
  }, [user?.id, refreshTrigger]); // refreshTrigger eklendi - modal kapandığında refetch

  // Hasta bilgilerini API'den çek
  useEffect(() => {
    const fetchPatientData = async () => {
      if (!patientId) return;

      try {
        const data = await nurseService.patient.getPatientInfo({ id: Number(patientId) });
        setPatientData(data);
      } catch (error) {
        console.error('❌ MC-LIST: Hasta bilgisi alınamadı:', error);
        setPatientData(null);
      }
    };

    fetchPatientData();
  }, [patientId]);

  // API'den gelen veriyi kullan, yoksa prop'tan gelen veriyi kullan
  const actualGender = patientData?.data?.sexuality || gender;
  const actualIsPregnant = patientData?.data?.pregnantorlactation === 'D' || isPregnant;

  // Helper function to get count by typename
  const getCountByTypeName = (typeName: string): number => {
    return generalDiseasesData?.counts?.[typeName] || 0;
  };

  // Helper function to get group by typename
  const getGroupByTypeName = (typeName: string): McGeneralDiseaseGroup | undefined => {
    return generalDiseasesData?.groups?.find((g) => g.typename === typeName);
  };

  // Tüm card konfigürasyonları - useMemo ile translation'ları güncel tut
  const cardConfigs: CardConfig[] = useMemo(() => [
    { typename: "5-Alerjiler (Vademecum)", title: t("sections.allergies"), showRadio: true, sectionKey: 'allergies', typeId: 1 }, // ALLERGIES
    { typename: "7-Alışkanlıklar", title: t("sections.habits"), sectionKey: 'habits', typeId: 2 }, // HABITS
    { typename: "10-Gebelik Durumu", title: t("sections.pregnancy"), sectionKey: 'pregnancy', genderSpecific: 'female', typeId: 10 }, // PREGNANT
    { typename: "2-Geçirilmiş Hastalıklar", title: t("sections.prev-diseases"), sectionKey: 'prev-diseases', typeId: 11 }, // PREVIUS_DISEASE
    { typename: "3-Geçirilmiş Ameliyatlar", title: t("sections.prev-surgeries"), sectionKey: 'prev-surgeries', typeId: 12 }, // PREVIUS_SURGERIES
    { typename: "4-Soygeçmiş", title: t("sections.family-history"), sectionKey: 'family-history', typeId: 7 }, // FAMILY_HISTORY
    { typename: "6-Hastalıklar", title: t("sections.diseases"), sectionKey: 'diseases', typeId: 4 }, // DISEASES
    { typename: "8-Kullandığı İlaçlar", title: t("sections.drugs"), sectionKey: 'drugs', typeId: 5 }, // DRUGS
    { typename: "9-Maruziyetler", title: t("sections.exposures"), sectionKey: 'exposures', typeId: 6 }, // EXPOSURES
    { typename: "1-Özel Notlar", title: t("sections.special-notes"), sectionKey: 'special-notes', typeId: 14 }, // SPECIAL_NOTES
    { typename: "-Tansiyon Takibi", title: t("sections.blood-pressure"), sectionKey: 'blood-pressure', typeId: 3 }, // BLOOD_PRESSURE
    { typename: "11-Nadir Hastalıklar", title: t("sections.rare-diseases"), sectionKey: 'rare-diseases', typeId: 13 }, // RARE_DISEASE
    { typename: "-Boy Kilo", title: t("sections.height-weight"), sectionKey: 'height-weight', typeId: 8 }, // HEIGHT_WEIGHT
    { typename: "-Diğer", title: t("sections.other"), sectionKey: 'other', typeId: 9 }, // OTHER
  ], [t]);

  // Tansiyon parse fonksiyonu
  const parseBloodPressure = (name: string | null): string => {
    if (!name) return '';

    // API'den gelen format: "Büyük : 176, Küçük : 226, Nabız : 482, Not : not"
    const systolicMatch = name.match(/Büyük\s*:\s*(\d+)/i);
    const diastolicMatch = name.match(/Küçük\s*:\s*(\d+)/i);

    const systolic = systolicMatch ? systolicMatch[1] : '';
    const diastolic = diastolicMatch ? diastolicMatch[1] : '';

    if (systolic && diastolic) {
      return `${systolic}/${diastolic}`;
    }

    return '';
  };

  // Her card için veri hazırlama
  const cardsData: Array<CardConfig & { count: number; hasData: boolean; displayItems: string[]; inPreferences: boolean }> = useMemo(() => {
    // Tercihler yüklenene kadar boş dizi döndür
    if (!preferencesLoaded) return [];

    // Cinsiyet kontrolü (normalize et)
    const normalizedGender = String(actualGender || '').toLowerCase();

    const isFemale = ['female', 'kadin', 'kadın', '2', 'k', 'bayan'].includes(normalizedGender) || Boolean(actualIsPregnant);

    // Gebelik durumu için typename kontrolü: Genellikle "Gebelik" ile ilgili bir grup olmayabilir,
    // generalPatientInfo'dan geliyordu diğer tarafta.
    // Ancak burada generalDiseasesData kullanılıyor.
    // Eğer gebelik cardConfigs'de varsa ve group içinde yoksa count 0 gelir.

    const filteredConfigs = cardConfigs.filter((config: CardConfig) => {
      // Cinsiyet filtresi
      if (config.genderSpecific === 'female' && !isFemale) return false;

      return true;
    });

    const data = filteredConfigs.map((config: CardConfig) => {
      const count = getCountByTypeName(config.typename);
      const group = getGroupByTypeName(config.typename);
      const items = group?.items || [];

      // Max 2 item göster
      const displayItems = items.slice(0, 2).map((item) => {
        // Tansiyon takibi için özel parse
        if (config.typename === "-Tansiyon Takibi") {
          return parseBloodPressure(item.name);
        }
        // Diğerleri için name field'ından veriyi al
        return item.name || '';
      }).filter((name) => name && name.trim() !== '');

      return {
        ...config,
        count,
        hasData: count > 0,
        displayItems,
        inPreferences: userPreferences.has(config.typeId!), // Tercihte var mı?
      };
    }).filter(card => {
      // Kullanıcı tercihlerinde varsa göster (boş olsa bile)
      // VEYA veri varsa her zaman göster (tercih bakılmaksızın)
      return userPreferences.has(card.typeId!) || card.hasData;
    });

    // Sıralama: Önce tercih listesindekiler (API sırasına göre), sonra sadece verisi olanlar
    const sortedData = [...data].sort((a, b) => {
      const indexA = orderedPreferences.indexOf(a.typeId!);
      const indexB = orderedPreferences.indexOf(b.typeId!);

      const aInPref = indexA !== -1;
      const bInPref = indexB !== -1;

      // İkisi de preferences'ta varsa, API sırasına göre
      if (aInPref && bInPref) return indexA - indexB;

      // Sadece A preferences'ta varsa, A önce gelir
      if (aInPref) return -1;

      // Sadece B preferences'ta varsa, B önce gelir
      if (bInPref) return 1;

      // İkisi de preferences'ta yoksa (sadece verisi var), orijinal sırayı koru
      return 0;
    });

    return sortedData;

  }, [generalDiseasesData, cardConfigs, actualGender, actualIsPregnant, patientData, userPreferences, orderedPreferences, preferencesLoaded]);

  // Alerjiler için özel kontrol
  const allergiesElement = cardsData.find((c) => c.typename === "5-Alerjiler (Vademecum)");
  const hasAllergies = (allergiesElement?.count || 0) > 0;

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        textAlign: "left",
        fontSize: "18px",
        color: "#111827",
        fontFamily: "Poppins",
      }}
    >
      <Box sx={{ position: "relative", width: "100%", fontSize: "12px", color: "rgba(0, 0, 0, 0.87)" }}>
        <Box sx={{ position: "relative", display: "flex", alignItems: "flex-start", gap: "22px", flexWrap: "wrap", flexShrink: 0 }}>
          {cardsData.map((card) => {
            const isAllergy = card.typename === "5-Alerjiler (Vademecum)";
            const commonContainerStyles = {
              position: "absolute",
              top: "16.58px",
              left: "14.87px",
              width: "259px",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "3px",
            };

            const isPregnancy = card.typename === "10-Gebelik Durumu";
            const hasPregnancy = (card.count || 0) > 0;

            // Gebelik durumu text'ini al
            const getPregnancyStatusText = () => {
              if (!patientData?.data?.pregnantorlactation) return '';

              const status = patientData.data.pregnantorlactation;
              // D: Hamile, E: Emzirme Dönemi, B: Doğum, S: Şüpheli, N: Hayır
              const statusMap: Record<string, string> = {
                'D': t('pregnancy.pregnant'),
                'E': t('pregnancy.breastfeeding'),
                'B': t('pregnancy.birth'),
                'S': t('pregnancy.suspicious'),
                'N': t('pregnancy.no'),
              };

              return statusMap[status] || '';
            };

            const commonTitleStyles = {
              flex: 1,
              minWidth: 0,
              position: "relative",
              lineHeight: 1,
              opacity: 0.6,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            };

            const commonCounterStyles = {
              height: "19px",
              width: "19px",
              borderRadius: "100px",
              backgroundColor: "#fcf4d9",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "4px",
              boxSizing: "border-box",
              color: "#ef9610",
              fontFamily: "Poppins",
              marginLeft: "auto",
              marginRight: "8px", // Added space from right
              flexShrink: 0
            };

            const handleCardClick = () => {
              if (onOpenGhiModal && card.sectionKey) {
                onOpenGhiModal(card.sectionKey);
              }
            };

            return (
              <Box
                key={card.typename}
                onClick={card.typename !== "5-Alerjiler (Vademecum)" ? handleCardClick : undefined} // Alerjiler için özel click handle
                sx={{
                  height: "105px",
                  width: "280px",
                  position: "relative",
                  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                  overflow: "hidden",
                  cursor: card.typename !== "5-Alerjiler (Vademecum)" ? "pointer" : "default",
                  transition: "transform 0.1s",
                  "&:hover": card.typename !== "5-Alerjiler (Vademecum)" ? {
                    transform: "scale(1.02)",
                    boxShadow: "0px 6px 6px rgba(0, 0, 0, 0.2)",
                  } : {}
                }}
              >
                <Box sx={commonContainerStyles}>
                  {/* Header Row */}
                  <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, fontSize: "12px", fontFamily: "Urbanist", width: "100%" }}>
                    <Box sx={{ height: "19px", width: "100%", position: "relative", display: "flex", alignItems: "center" }}>
                      <Box sx={commonTitleStyles}>{card.title}</Box>
                      <Box sx={commonCounterStyles}>
                        <Box component="b" sx={{
                          position: "relative",
                          letterSpacing: "-0.02em",
                          lineHeight: "16px",
                          flexShrink: 0,
                          fontSize: "12px",
                        }}>
                          {card.count}
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ alignSelf: "stretch", height: "1px", position: "relative", borderTop: "1px solid #e9ecf2", boxSizing: "border-box", flexShrink: 0 }} />

                  {/* Content Area */}
                  {isAllergy ? (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "flex-start",
                        marginTop: "8px"
                      }}
                    >
                      <Box
                        onClick={() => {
                          if (onOpenGhiModal) onOpenGhiModal('allergies');
                        }}
                        sx={{ width: "81px", display: "flex", alignItems: "center", gap: "4px", cursor: 'pointer' }}
                      >
                        <Box sx={{ overflow: "hidden", display: "flex", alignItems: "center" }}>
                          <Radio checked={hasAllergies} size="small" sx={{ padding: "0px", pointerEvents: 'none' }} />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          <Box sx={{ position: "relative", letterSpacing: "0.15px", lineHeight: "150%", fontSize: "12px", marginLeft: "4px" }}>
                            {t("allergy.allergy-status-var")}
                          </Box>
                        </Box>
                      </Box>
                      <Box
                        // onClick={() => {
                        //     // Handle "Yok" click - future implementation
                        // }}
                        sx={{ width: "87px", display: "flex", alignItems: "center", marginLeft: "12px", position: "relative", gap: "4px", cursor: 'pointer' }}
                      >
                        <Box sx={{ overflow: "hidden", display: "flex", alignItems: "center", flexShrink: 0 }}>
                          <Radio checked={!hasAllergies} size="small" sx={{ padding: "0px", pointerEvents: 'none' }} />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", flexShrink: 0 }}>
                          <Box sx={{ position: "relative", letterSpacing: "0.15px", lineHeight: "150%", fontSize: "12px", marginLeft: "4px" }}>
                            {t("allergy.allergy-status-none")}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ) : isPregnancy ? (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "flex-start",
                        marginTop: "8px"
                      }}
                    >
                      <Box
                        onClick={() => {
                          if (onOpenGhiModal) onOpenGhiModal('pregnancy');
                        }}
                        sx={{
                          width: "100%",
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8
                          }
                        }}
                      >
                        <Box sx={{ fontSize: "12px", lineHeight: "150%", letterSpacing: "0.15px" }}>
                          {getPregnancyStatusText() || '-'}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    card.displayItems.map((item, index) => (
                      <Box key={index} sx={{ width: "100%", height: "20px", position: "relative", flexShrink: 0 }}>
                        <Box sx={{ position: "absolute", top: "0px", left: "0px", width: "100%", height: "20px" }}>
                          <Box sx={{ position: "absolute", top: "0px", left: "0px", width: "100%", display: "flex", alignItems: "flex-start" }}>
                            <Box sx={{
                              width: "100%",
                              position: "relative",
                              lineHeight: 1,
                              fontWeight: 500,
                              display: "inline-block",
                              flexShrink: 0,
                              fontSize: "12px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}>
                              {item}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default McList;
